/* eslint-disable no-console, no-alert */

import React, { useState, useCallback } from 'react';
import {
  Container, Row, Col, Table, Badge, Button as BSButton,
} from 'react-bootstrap';
import AnomalyMap from './index';

// Mock data unificado para mapas e tabela
const initialData = [
  {
    id: '1998',
    title: '01/1998',
    dataSource: 'Data: MERGE-CPTEC – Precipitation Anomaly – January 1998',
    imageUrl: '/mapa.png',
    ano: 1998,
    mes: 1,
    nino34: 2.11,
    nino4: 0.49,
    iod: 0.26,
    tna: 0.47,
    tsa: 0.29,
    tad: 1.16,
    sah: 0.05,
    sac: 0.49,
    pdo: 1.40,
    aao: 0.41,
    visible: true,
  },
  {
    id: '1999',
    title: '01/1999',
    dataSource: 'Data: MERGE-CPTEC – Precipitation Anomaly – January 1999',
    imageUrl: '/mapa.png',
    ano: 1999,
    mes: 1,
    nino34: -1.64,
    nino4: -0.49,
    iod: -0.63,
    tna: -0.54,
    tsa: 0.03,
    tad: -0.76,
    sah: -0.18,
    sac: 0.00,
    pdo: -1.08,
    aao: 1.00,
    visible: true,
  },
  {
    id: '2000',
    title: '01/2000',
    dataSource: 'Data: MERGE-CPTEC – Precipitation Anomaly – January 2000',
    imageUrl: '/mapa.png',
    ano: 2000,
    mes: 1,
    nino34: -1.80,
    nino4: 0.15,
    iod: -0.41,
    tna: -0.26,
    tsa: -0.24,
    tad: -0.07,
    sah: -0.20,
    sac: -0.33,
    pdo: -2.60,
    aao: 1.27,
    visible: true,
  },
];

/**
 * Função para determinar a classe CSS baseada no valor
 */
function getValueClass(value: number): string {
  if (value > 0) return 'text-danger fw-bold';
  if (value < 0) return 'text-primary fw-bold';
  return '';
}

/**
 * Componente de demonstração do AnomalyMap
 * Mostra 3 instâncias do componente lado a lado com tabela de dados embaixo
 */
export default function AnomalyMapDemo() {
  const [data, setData] = useState(initialData);
  const [alerts, setAlerts] = useState<Array<{ type: string; message: string }>>([]);

  // Filtrar mapas e linhas da tabela visíveis
  const visibleMaps = data.filter((item) => item.visible);

  // Handler para fechar mapa
  const handleMapClose = useCallback((mapId: string) => {
    setData((prevData) => (
      prevData.map((item) => (
        item.id === mapId
          ? { ...item, visible: false }
          : item
      ))
    ));

    // Mostrar feedback
    const mapTitle = data.find((item) => item.id === mapId)?.title;
    setAlerts([{
      type: 'info',
      message: `Mapa ${mapTitle} foi fechado - linha correspondente removida da tabela`,
    }]);

    // Limpar alerta após 3 segundos
    setTimeout(() => setAlerts([]), 3000);
  }, [data]);

  // Handler para restaurar todos os mapas
  const handleRestoreAll = useCallback(() => {
    setData((prevData) => (
      prevData.map((item) => ({ ...item, visible: true }))
    ));
    setAlerts([]);
  }, []);

  const visibleCount = visibleMaps.length;
  const totalCount = data.length;

  return (
    <Container className="py-4">
      <Row>
        <Col>
          <h1>Demo: AnomalyMap Component</h1>
          <p className="lead">
            Demonstração do componente com layout similar à imagem de referência
            {' '}
            - 3 mapas lado a lado com tabela de dados embaixo.
          </p>

          {/* Controles */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <Badge bg="primary" className="me-2">
                {visibleCount}
                {' '}
                de
                {' '}
                {totalCount}
                {' '}
                mapas visíveis
              </Badge>
              {visibleCount < totalCount && (
                <BSButton
                  size="sm"
                  variant="outline-success"
                  onClick={handleRestoreAll}
                >
                  Restaurar Todos
                </BSButton>
              )}
            </div>
            <div>
              <BSButton
                variant="outline-secondary"
                size="sm"
                onClick={() => setAlerts([])}
                disabled={alerts.length === 0}
              >
                🧹 Limpar filtros
              </BSButton>
            </div>
          </div>

          {/* Alertas */}
          {alerts.map((alert) => (
            <div
              key={`alert-${alert.type}-${alert.message}`}
              className={`alert alert-${alert.type} alert-dismissible`}
            >
              {alert.message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setAlerts([])}
                aria-label="Close"
              />
            </div>
          ))}
        </Col>
      </Row>

      {/* Seção dos Mapas - Layout horizontal como na imagem */}
      <Row className="mb-5 g-2">
        {visibleMaps.length === 0 && (
          <Col>
            <div className="text-center py-5 text-muted border rounded">
              <div className="mb-3">
                <i className="fas fa-map fa-3x opacity-50" />
              </div>
              <p className="lead">Nenhum mapa visível</p>
              <p>
                Todos os mapas foram fechados. Use
                {' '}
                &quot;Restaurar Todos&quot;
                {' '}
                para visualizá-los novamente.
              </p>
            </div>
          </Col>
        )}

        {visibleMaps.map((map) => (
          <Col key={map.id} lg={4} md={6} className="mb-3">
            <AnomalyMap
              title={map.title}
              dataSource={map.dataSource}
              imageUrl={map.imageUrl}
              onClose={() => handleMapClose(map.id)}
            />
          </Col>
        ))}
      </Row>

      {/* Seção da Tabela - Como na imagem de referência */}
      <Row>
        <Col>
          <div className="border rounded p-3 bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Dados Climáticos</h5>
              <small className="text-muted">
                Índices oceânico-atmosféricos correspondentes aos períodos dos mapas
              </small>
            </div>

            <div className="table-responsive">
              <Table size="sm" hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Ano</th>
                    <th>Mês</th>
                    <th>
                      NIÑO3.4
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      NIÑO4
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      IOD
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      TNA
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      TSA
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      TAD
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      SAH
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      SAC
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      PDO
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                    <th>
                      AAO
                      {' '}
                      <i className="fas fa-sort text-muted" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMaps.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.ano}</strong>
                      </td>
                      <td>{row.mes}</td>
                      <td className={getValueClass(row.nino34)}>
                        {row.nino34.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.nino4)}>
                        {row.nino4.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.iod)}>
                        {row.iod.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.tna)}>
                        {row.tna.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.tsa)}>
                        {row.tsa.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.tad)}>
                        {row.tad.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.sah)}>
                        {row.sah.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.sac)}>
                        {row.sac.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.pdo)}>
                        {row.pdo.toFixed(2)}
                      </td>
                      <td className={getValueClass(row.aao)}>
                        {row.aao.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="mt-3 p-2 bg-light rounded">
              <small className="text-muted">
                <strong>Legenda:</strong>
                {' '}
                <span className="text-danger">Valores positivos</span>
                {' '}
                indicam condições acima da média,
                {' '}
                <span className="text-primary">valores negativos</span>
                {' '}
                indicam condições abaixo da média.
                Os ícones de ordenação permitem classificar os dados por qualquer coluna.
              </small>
            </div>
          </div>
        </Col>
      </Row>

      <hr className="my-5" />

      {/* Documentação de Uso */}
      <Row>
        <Col>
          <div className="bg-light p-4 rounded">
            <h4>Como Usar o Componente:</h4>

            <h6>Características do AnomalyMap:</h6>
            <ul>
              <li>
                <strong>Componente Individual:</strong>
                {' '}
                Cada mapa é uma instância independente
              </li>
              <li>
                <strong>Card com Header:</strong>
                {' '}
                Título personalizado e botão de fechar
              </li>
              <li>
                <strong>Imagem Responsiva:</strong>
                {' '}
                Se adapta ao tamanho do container
              </li>
              <li>
                <strong>Legenda Integrada:</strong>
                {' '}
                Escala de cores vertical no canto direito
              </li>
              <li>
                <strong>Tratamento de Erros:</strong>
                {' '}
                Placeholder automático para imagens quebradas
              </li>
              <li>
                <strong>Acessibilidade:</strong>
                {' '}
                Alt text e controles keyboard-friendly
              </li>
            </ul>

            <h6>Exemplo de Implementação:</h6>
            <pre className="bg-dark text-light p-3 rounded mt-3" style={{ fontSize: '0.85rem' }}>
              {`import AnomalyMap from 'components/DesignSystem/AnomalyMap';

function MyComponent() {
  const handleMapClose = () => {
    console.log('Mapa fechado');
    // Implementar lógica de fechamento
  };

  return (
    <Row>
      <Col md={4}>
        <AnomalyMap
          title="01/1998"
          dataSource="Data: MERGE-CPTEC – Precipitation Anomaly – January 1998"
          imageUrl="/path/to/map-1998.jpg"
          onClose={handleMapClose}
        />
      </Col>
      <Col md={4}>
        <AnomalyMap
          title="01/1999"
          dataSource="Data: MERGE-CPTEC – Precipitation Anomaly – January 1999"
          imageUrl="/path/to/map-1999.jpg"
          onClose={handleMapClose}
        />
      </Col>
      <Col md={4}>
        <AnomalyMap
          title="01/2000"
          dataSource="Data: MERGE-CPTEC – Precipitation Anomaly – January 2000"
          imageUrl="/path/to/map-2000.jpg"
          onClose={handleMapClose}
        />
      </Col>
    </Row>
  );
}`}
            </pre>

            <h6>Props Disponíveis:</h6>
            <ul className="mb-0">
              <li>
                <code>title</code>
                : Título exibido no header (ex:
                {' '}
                &quot;01/1998&quot;
                )
              </li>
              <li>
                <code>dataSource</code>
                : Texto informativo sobre a fonte dos dados
              </li>
              <li>
                <code>imageUrl</code>
                : URL da imagem do mapa climático
              </li>
              <li>
                <code>onClose</code>
                : Callback executado ao clicar no botão
                {' '}
                &quot;X&quot;
              </li>
              <li>
                <code>className</code>
                : Classes CSS adicionais (opcional)
              </li>
            </ul>
          </div>
        </Col>
      </Row>
    </Container>
  );
}
